import type { Prefs } from './prefs';

/**
 * Settings with steps. Each is one tile: a click moves to the next step, and
 * after the last one back to the default. The bars under the label show the
 * step. Values are listed in click order; the first is the default (off).
 */
export const CYCLES = {
  textSize: [0, 1, 2, 3],
  font: ['none', 'readable', 'dyslexic'],
  align: ['none', 'left', 'center', 'right'],
  contrast: ['none', 'dark', 'light', 'invert'],
  saturation: ['none', 'low', 'high', 'gray'],
} as const;

export type CycleKey = keyof typeof CYCLES;

/** 0 is off; 1 to n are the steps. An unknown value counts as off. */
export function stepOf<K extends CycleKey>(key: K, value: Prefs[K]): number {
  const list = CYCLES[key] as unknown as readonly Prefs[K][];
  return Math.max(0, list.indexOf(value));
}

export function stepCount(key: CycleKey): number {
  return CYCLES[key].length - 1;
}

/** The value after `value`, wrapping from the last step to the default. */
export function nextValue<K extends CycleKey>(
  key: K,
  value: Prefs[K],
): Prefs[K] {
  const list = CYCLES[key] as unknown as readonly Prefs[K][];
  return list[(stepOf(key, value) + 1) % list.length];
}

/**
 * The panel's tiles, in grid order (three a row): reading and fonts, text
 * layout, color, then the pointer and motion aids.
 */
export const TILE_ORDER = [
  'speech',
  'textSize',
  'font',
  'align',
  'spacing',
  'links',
  'contrast',
  'saturation',
  'images',
  'guide',
  'cursor',
  'motion',
] as const;

export type TileKey = (typeof TILE_ORDER)[number];

export function isCycle(key: TileKey): key is CycleKey {
  return Object.prototype.hasOwnProperty.call(CYCLES, key);
}
